import { useId, useState } from 'react';
import type { TaskSummary } from '../../lib/tasks';
import { Input } from '../ui';

/**
 * Searchable task picker built on a native `<datalist>`, following TagInput
 * rather than adding a combobox dependency.
 *
 * The wrinkle TagInput does not have: a datalist hands back the option's *text*,
 * but a task is an entity with an id. So the label has to identify the task on
 * its own, and the commit maps it back. Options come from the list already in
 * memory -- no search endpoint, no debounce.
 */
export function TaskCombobox({
  id,
  label,
  options,
  placeholder = 'Search tasks…',
  compact = false,
  onSelect,
}: {
  /** So a surrounding `<label>` points at the box people type in. */
  id?: string;
  label: string;
  options: TaskSummary[];
  placeholder?: string;
  /** Shorter, for the property sheet. The prerequisites picker keeps full height. */
  compact?: boolean;
  onSelect: (task: TaskSummary) => void;
}) {
  const [draft, setDraft] = useState('');
  const [missed, setMissed] = useState(false);
  const listId = useId();

  // Titles are not unique, so duplicates get a short id -- but only when there
  // is something to disambiguate, so the common case stays clean.
  const labelFor = (task: TaskSummary) =>
    options.filter((other) => other.title === task.title).length > 1
      ? `${task.title} (#${task.id.slice(0, 4)})`
      : task.title;

  function commit(value: string) {
    const match = options.find((task) => labelFor(task) === value);
    if (!match) {
      // Typing is not a failure; only a committed non-match is.
      setMissed(value.trim().length > 0);
      return;
    }
    setDraft('');
    setMissed(false);
    onSelect(match);
  }

  return (
    <div>
      <Input
        compact={compact}
        id={id}
        list={listId}
        value={draft}
        aria-label={label}
        placeholder={placeholder}
        onChange={(event) => {
          const { value } = event.target;
          setDraft(value);
          setMissed(false);
          // Picking from the datalist fires `change` with the whole label at
          // once, so an exact hit commits without needing Enter.
          if (options.some((task) => labelFor(task) === value)) commit(value);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          commit(draft);
        }}
      />
      <datalist id={listId}>
        {options.map((task) => (
          <option key={task.id} value={labelFor(task)} />
        ))}
      </datalist>
      {missed ? <p className="mt-1 text-dense text-stone-500">No task matches that.</p> : null}
    </div>
  );
}
